import React, { useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import { useSelector, useDispatch } from "react-redux";

import { VideoManagerProvider, useVideoManager } from "../../contexts/VideoManagerContext";
import { selectIsAudioEnabled, toggleAudio } from "../../app/actions/audio/audioSlice";

import OsTitleBlock from "../../components/block/OsTitleBlock";
import OsHeroImage from "./components/OsHeroImage";
import OsHeroMessage from "./components/OsHeroMessage";
import OsBrandName from "./components/OsBrandName";
import OsFutureSection from "./components/OsFutureSection";
import OsFooter from "./components/OsFooter";
import OsCard from "./components/OsCard";
import OsRelease from "./components/OsRelease";
import OsAudioToggle from "./components/OsAudioToggle";
// MP4版
import osVideo2 from "../../assets/videos/os/2_３つの役割.mp4";
// import osVideo3 from "../../assets/videos/os/3_プロジェクトを始める.mp4";
import osVideo4 from "../../assets/videos/os/4_ホーム.mp4";
import osVideo5 from "../../assets/videos/os/5_プレビュー.mp4";
import osVideo6 from "../../assets/videos/os/6_トークルーム.mp4";
import osVideo7 from "../../assets/videos/os/7_dm.mp4";
import osVideo8 from "../../assets/videos/os/8_オファー.mp4";
import osVideo9 from "../../assets/videos/os/9_PL.mp4";
import osVideo10 from "../../assets/videos/os/10_支払い.mp4";

// HLS版（.segment拡張子使用）
// 注意：GitHub ActionsとS3の制限により、日本語ディレクトリ名は使用できません
// ディレクトリ名を英数字に変更済み
const osVideo2Hls = "/assets/media/hls/os/2_three_roles/master.m3u8";
// const osVideo3Hls = "/assets/media/hls/os/3_start_project/master.m3u8";
const osVideo4Hls = "/assets/media/hls/os/4_home/master.m3u8";
const osVideo5Hls = "/assets/media/hls/os/5_preview/master.m3u8";
const osVideo6Hls = "/assets/media/hls/os/6_talk_room/master.m3u8";
const osVideo7Hls = "/assets/media/hls/os/7_DM/master.m3u8";
const osVideo8Hls = "/assets/media/hls/os/8_offer/master.m3u8";
const osVideo9Hls = "/assets/media/hls/os/9_PL/master.m3u8";
const osVideo10Hls = "/assets/media/hls/os/10_payment/master.m3u8";
import osAudioEn from "../../assets/audio/os/soremo_os_en.mp3";
import osMcp from "../../assets/images/os/os_mcp.webp";
import osMileage from "../../assets/images/os/os_mileage.webp";
import osMgk from "../../assets/images/os/os_mgk.webp";
import osProfile from "../../assets/images/os/os_profile.webp";
import osMemberlist from "../../assets/images/os/os_memberlist.webp";
import osSchedule from "../../assets/images/os/os_schedule.webp";
import osContract from "../../assets/images/os/os_contract.webp";
import osCredits from "../../assets/images/os/os_credits.webp";

// HLS再生を有効にするフラグ（trueにするとHLS版を使用）
// ディレクトリ名を英数字に変更し、デプロイ問題を解決済み
const USE_HLS = true;

// Styled Components
const OsMain = styled.main`
  background-color: var(--soremo-background);
  
  /* モバイルでのスクロール最適化 */
  @media (max-width: 768px) {
    /* スクロールジャンプ防止 */
    -webkit-overflow-scrolling: touch;
    overflow-anchor: none; /* スクロールアンカリングを無効化 */
  }
`;

// メインコンテンツコンポーネント（VideoManagerコンテキストを使用）
const OsContent = () => {
  // 「まだスタート前かどうか」を管理する状態
  const [isStarted, setIsStarted] = useState(false);
  // Reduxからオーディオの有効/無効状態を取得
  const isAudioEnabled = useSelector(selectIsAudioEnabled);
  const isMobile = useSelector((state) => state.device.isMobile);
  const dispatch = useDispatch();
  const audioRefEn = useRef(null);
  const [isPlayingEn, setIsPlayingEn] = useState(false);
  const targetRef = useRef(null); // 発火のタイミングに使いたい要素
  const openingAreaRef = useRef(null); // オープニングBGMエリア
  const [isInOpeningArea, setIsInOpeningArea] = useState(false);
  const [isInEndingArea, setIsInEndingArea] = useState(false);
  
  // VideoManagerから動画再生状態を取得
  const { hasActiveVideo } = useVideoManager();

  // 最初からスクロールを有効化 & スクロールスナップの設定
  useEffect(() => {
    document.body.style.overflow = "auto";
    
    // モバイルではスクロールスナップを無効化（ジャンプ防止）
    if (!isMobile) {
      // PCのみスクロールスナップを使用
      document.documentElement.style.scrollSnapType = "y proximity";
      document.documentElement.style.scrollBehavior = "smooth";
      document.body.style.scrollSnapType = "y proximity";
    } else {
      // モバイルではスムーズスクロールのみ
      document.documentElement.style.scrollBehavior = "auto"; // smoothだと遅延が発生
      document.body.style.scrollBehavior = "auto";
      // iOS用の設定
      document.body.style.webkitOverflowScrolling = "touch";
    }
    
    // クリーンアップ関数でスタイルをリセット
    return () => {
      document.documentElement.style.scrollSnapType = "";
      document.documentElement.style.scrollBehavior = "";
      document.body.style.scrollSnapType = "";
      document.body.style.webkitOverflowScrolling = "";
    };
  }, [isMobile]);

  // オーディオのON/OFF切り替え
  const handleAudioToggle = useCallback(() => {
    dispatch(toggleAudio());
  }, [dispatch]);

  const handleFadeInAudioEn = useCallback(() => {
    if (!audioRefEn.current || isPlayingEn || !isAudioEnabled) return;

    let fadeVolume = 0.0;
    audioRefEn.current.volume = fadeVolume;
    audioRefEn.current.muted = !isAudioEnabled;
    audioRefEn.current.play();
    setIsPlayingEn(true);

    const fadeInterval = setInterval(() => {
      fadeVolume = fadeVolume + 0.1;
      if (fadeVolume >= 1.0) {
        audioRefEn.current.volume = 1.0;
        clearInterval(fadeInterval);
      } else {
        audioRefEn.current.volume = fadeVolume;
      }
    }, 200);
  }, [isPlayingEn, isAudioEnabled]);

  const handleFadeOutAudioEn = useCallback(() => {
    if (!audioRefEn.current || !isPlayingEn) return;

    let fadeVolume = audioRefEn.current.volume;
    const fadeInterval = setInterval(() => {
      fadeVolume = fadeVolume - 0.1;
      if (fadeVolume <= 0.0) {
        audioRefEn.current.volume = 0.0;
        audioRefEn.current.pause();
        clearInterval(fadeInterval);
        setIsPlayingEn(false);
      } else {
        audioRefEn.current.volume = fadeVolume;
      }
    }, 200);
  }, [isPlayingEn]);

  // エンディングBGMの再生制御
  useEffect(() => {
    const observedTarget = targetRef.current;
    if (!("IntersectionObserver" in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsInEndingArea(entry.isIntersecting);
          
          // 動画が再生中の場合はBGMを再生しない
          if (entry.isIntersecting && isAudioEnabled && !hasActiveVideo) {
            handleFadeInAudioEn();
          } else if (!entry.isIntersecting || hasActiveVideo) {
            handleFadeOutAudioEn();
          }
        });
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      },
    );

    if (observedTarget) {
      observer.observe(observedTarget);
    }

    return () => {
      if (observedTarget) {
        observer.unobserve(observedTarget);
      }
    };
  }, [isStarted, handleFadeInAudioEn, handleFadeOutAudioEn, isAudioEnabled, isPlayingEn, hasActiveVideo]);
  
  // オープニングエリアの検出
  useEffect(() => {
    const observedTarget = openingAreaRef.current;
    if (!("IntersectionObserver" in window) || !observedTarget) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsInOpeningArea(entry.isIntersecting);
        });
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      },
    );

    observer.observe(observedTarget);

    return () => {
      observer.unobserve(observedTarget);
    };
  }, []);

  // オーディオ状態が変更されたときにBGMのmuted属性を更新
  useEffect(() => {
    if (audioRefEn.current) {
      audioRefEn.current.muted = !isAudioEnabled;
    }
  }, [isAudioEnabled]);
  
  // 動画再生状態が変わったときにBGMを制御
  useEffect(() => {
    if (hasActiveVideo && isPlayingEn) {
      handleFadeOutAudioEn();
    } else if (!hasActiveVideo && isInEndingArea && isAudioEnabled) {
      handleFadeInAudioEn();
    }
  }, [hasActiveVideo, isInEndingArea, isAudioEnabled, handleFadeInAudioEn, handleFadeOutAudioEn, isPlayingEn]);

  return (
    <OsMain className="u-col-center u-bg-background">
      <div ref={openingAreaRef} className="u-w100 u-col-center">
        <OsHeroImage 
          isStarted={isStarted} 
          onStart={() => setIsStarted(true)}
          isAudioEnabled={isAudioEnabled}
          onAudioToggle={handleAudioToggle}
          hasActiveVideo={hasActiveVideo}
          isInOpeningArea={isInOpeningArea}
        />

        {isStarted && (
          <>
            <OsHeroMessage />
            <OsBrandName />
            <OsFutureSection />
          </>
        )}
      </div>

      {isStarted && (
        <>
          <div className="u-wrapper">
              <OsTitleBlock number="1" title="３つの役割" />
              <OsCard title="" src={USE_HLS ? osVideo2Hls : osVideo2}>
                soremo OSでは、プロジェクトごとに「3つの役割」を設定します。
                <br />
                その中でも、「プロデューサー」の存在により、クライアントにとっては、状況に応じた柔軟な進行が可能になり、スペシャリストにとっては、制作に集中できる環境が整います。結果として、プロジェクト全体が安定します。
                <br />
              </OsCard>

              {/* <OsTitleBlock number="2" title="プロジェクトを始める" />
              <OsCard title="" src={osVideo3}>
                かんたん3ステップで、どなたでもすぐにプロジェクトを立ち上げられます。
              </OsCard> */}

              <OsTitleBlock number="2" title="クライアントワーク" />

              <div className="u-mb16 todo-list-container">
                <OsCard title="ホーム" src={USE_HLS ? osVideo4Hls : osVideo4}>
                  クライアントとプロデューサーが納品物をやりとりする画面。
                  <br />
                  1ページで全体が見渡せるようになっており、やることリストで「これって、返信したっけ？」の不安がなくなります。
                </OsCard>
                <OsCard title="プレビュー" src={USE_HLS ? osVideo5Hls : osVideo5}>
                  ストリーミングで納品物をチェックできます。
                  <br />
                  複数テイクをワンクリックで比較しやすく、検収もチェックボタンでOK。プログレスメーターで全体の進捗も明快です。
                </OsCard>
                <OsCard title="トークルーム" src={USE_HLS ? osVideo6Hls : osVideo6}>
                  クライアントとプロデューサーとでグループチャット。
                  <br />
                  メッセージとファイルがいつも一緒。
                  <br />
                  バッジ通知で、着実にファイルの受け渡しできます。
                </OsCard>
              </div>

              <OsTitleBlock number="3" title="スペシャリストワーク" />
              <div className="u-mb16 todo-list-container">
                <OsCard title="DM" src={USE_HLS ? osVideo7Hls : osVideo7} >
                  スペシャリストとプロデューサーは、１対１のDMでやりとり。プライバシーが守られ、そのまま契約、大きなファイルの受け渡しまで完結できます。
                </OsCard>

                <OsCard title="再オファー" src={USE_HLS ? osVideo8Hls : osVideo8}>
                  スペシャリストは、プロデューサーからオファーを受け取ります。
                  そのオファーがプロデューサーに承認された場合は、引き受けた仕事の一部を他のスペシャリストへ再オファーが可能です。<br />
                  この仕組みによって、それぞれが専門に集中しつつ、綿密に連携しながら拡張していくチームワークを実現します。
                </OsCard>
                <OsCard title="PL" src={USE_HLS ? osVideo9Hls : osVideo9}>
                  まるで家計簿アプリのように。予算管理できます。俊敏でキレのある予算投下をそっと後押し。
                  <br />
                </OsCard>
                <OsCard title="支払い" src={USE_HLS ? osVideo10Hls : osVideo10}>
                  支払いは驚くほどシンプル。
                  <br />
                  手間のかかる部分はすべて代行。トラブルも解消します。
                </OsCard>
              </div>

              <OsTitleBlock number="4" title="そのほかの機能" />
              <audio ref={audioRefEn} src={osAudioEn} loop />
              <div className="todo-list-container u-gap16 u-w100" ref={targetRef}>
              <OsCard
                  title="MCP接続"
                  src={osMcp}
                  size="halfgray"
                >
                  AIチャットと接続できるようになりました。
                  <br />
                  「次の納品予定は？」「来週の香盤表は？」と訊ねるだけで、必要な情報にすぐアクセス。
                  <br />
                  画面を切り替えることなく、多段タスクを一発解決します。
                </OsCard>

                <OsCard
                  title="メンバーリスト"
                  src={osMemberlist}
                  size="halfgray"
                >
                  クライアントとプロデューサーを一覧表示。個別権限設定やIP制限ができ。マシンID監視によるお知らせも備わっています。
                </OsCard>

                <OsCard title="まもなく納品" src={osSchedule} size="halfgray">
                  納品予定日をセットするだけでスケジュールができあがる。リテイクも連動。マイルストーンを設定すると残り日数がカウントダウン。
                </OsCard>

                <OsCard
                  title="このプロジェクトの契約"
                  src={osContract}
                  size="halfgray"
                >
                  クライアントから依頼されたプロジェクトなら、クライアント責任者を招待し、見積書の提出から本契約の締結までスムーズに完結。
                </OsCard>

                <OsCard
                  title="スタッフクレジット"
                  src={osCredits}
                  size="halfgray"
                >
                  オファー履歴からアーティスト名（日本語・英語）を正確に取得し、ドラッグ&ドロップで並べ替え。PDFやテキストで出力。
                </OsCard>

                <OsCard title="楽曲認識" src={osMgk} size="halfgray">
                  オーディオデータをアップロードするだけで、既存楽曲の類似性をリサーチ。著作権侵害リスクを最小化します。
                </OsCard>

                <OsCard title="マイレージ" src={osMileage} size="halfgray">
                  利用料は、取引実績に応じてユーザーごとに個別で設定されます。
                  <br />
                  実績が蓄積された優良ユーザーほど、利用料も安くなる仕組みです。
                </OsCard>

                <OsCard title="マイプロフィール" src={osProfile} size="halfgray">
                  プロフィールサイトを作成し、プロフィールや作品、各種リンク先をひとまとめにできます。
                  <br />
                </OsCard>
              </div>
            </div>
          <OsRelease />
          <OsFooter />
        </>
      )}

      {/* フローティングオーディオボタン */}
      <OsAudioToggle
        isPlaying={isAudioEnabled}
        onToggle={handleAudioToggle}
        isVisible={isMobile ? (isInOpeningArea || isInEndingArea) : true}
      />
    </OsMain>
  );
};

// ラッパーコンポーネント
const Os = () => {
  return (
    <VideoManagerProvider>
      <OsContent />
    </VideoManagerProvider>
  );
};

export default Os;
